import { z } from 'zod'
import { isValidIsbn, normalizeIsbn } from './isbn'

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Use at most ${max} characters.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()

export const bookSchema = z.object({
  title: z.string().trim().min(1, 'Enter the title.').max(200),
  subtitle: optionalText(200),
  authors: z.string().trim().min(1, 'Enter the author(s).').max(255),
  isbn: z
    .string()
    .trim()
    .refine((v) => v === '' || isValidIsbn(v), 'Enter a valid ISBN-10 or ISBN-13.')
    .transform((v) => (v === '' ? null : normalizeIsbn(v))),
  publisher: optionalText(150),
  publishedYear: z
    .string()
    .trim()
    .refine(
      (v) => v === '' || (/^\d{4}$/.test(v) && Number(v) >= 1400 && Number(v) <= 2100),
      'Enter a year between 1400 and 2100.',
    )
    .transform((v) => (v === '' ? null : Number(v))),
  categoryId: z.string().transform((v) => (v === '' ? null : Number(v))),
  language: optionalText(40),
  shelfLocation: optionalText(40),
  totalCopies: z
    .string()
    .trim()
    .refine(
      (v) => /^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 10000,
      'Enter between 1 and 10000 copies.',
    )
    .transform(Number),
  description: optionalText(5000),
  active: z.boolean(),
  coverFileId: z.number().nullable(),
  removeCover: z.boolean(),
})

export type BookFormInput = z.input<typeof bookSchema>
export type BookFormOutput = z.output<typeof bookSchema>

export const settingsSchema = z.object({
  loanDays: z.coerce
    .number<string>()
    .int('Whole days only.')
    .min(1, 'At least 1 day.')
    .max(365, 'At most 365 days.'),
  maxActiveLoansStudent: z.coerce.number<string>().int().min(1, 'At least 1.').max(50, 'At most 50.'),
  maxActiveLoansStaff: z.coerce.number<string>().int().min(1, 'At least 1.').max(50, 'At most 50.'),
  finePerDay: z.coerce.number<string>().min(0, 'Cannot be negative.').max(1000, 'At most 1000.'),
})
export type SettingsFormInput = z.input<typeof settingsSchema>
export type SettingsFormOutput = z.output<typeof settingsSchema>

export const categorySchema = z.object({ name: z.string().trim().min(1, 'Enter a category name.').max(80) })
export type CategoryFormInput = z.infer<typeof categorySchema>
